import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  Image as ChakraImage,
  Input,
} from '@chakra-ui/react';
import { getProducts, publishProduct, getProduct as fetchProduct, generateMockup, getMockupStatus } from '../services/printifyService';
import { useCart } from '../context/CartContext';
import { useLocation } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { FaCheckCircle } from 'react-icons/fa';

const shopId = import.meta.env.VITE_PRINTIFY_SHOP_ID;

const ProductsPage = () => {
  const [printifyProducts, setPrintifyProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentDetails, setCurrentDetails] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { addToCart } = useCart();
  const location = useLocation();
  const [userImages, setUserImages] = useState([]);
  const [mockupTaskId, setMockupTaskId] = useState(null);
  const [mockupLoading, setMockupLoading] = useState(false);
  const [mockupImage, setMockupImage] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      if (!shopId) return;
      try {
        setLoadingProducts(true);
        const res = await getProducts(shopId, { limit: 20, status: 'published' });
        setPrintifyProducts(res?.data || res);
      } catch (err) {
        console.error('Failed to load Printify products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  // Pre-select image via ?src=…
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preload = params.get('src');
    if (preload) setSelectedImageUrl(preload);
  }, [location.search]);

  const openProductModal = async (prod) => {
    try {
      setCurrentProduct(prod);
      setCurrentDetails(null);
      setSelectedVariant('');
      setSelectedImageUrl('');
      onOpen();

      const details = await fetchProduct(shopId, prod.id);
      setCurrentDetails(details);
      if (details?.variants?.length) setSelectedVariant(details.variants[0].id);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: uploads } = await supabase
          .from('user_uploads')
          .select('file_url, file_url_wm')
          .eq('user_id', user.id)
          .eq('status', 'active');
        const imgs = (uploads || []).map(u => u.file_url_wm || u.file_url);
        setUserImages(imgs);
      }
    } catch (err) {
      console.error('Failed to fetch product details', err);
    }
  };

  const handleAddToCartPrintify = () => {
    if (!currentProduct || !selectedVariant || !selectedImageUrl) {
      alert('Please choose variant and image');
      return;
    }
    addToCart({
      id: `${currentProduct.id}-${selectedVariant}`,
      type: 'printify',
      productId: currentProduct.id,
      variantId: selectedVariant,
      previewUrl: selectedImageUrl,
      name: currentProduct.title,
      price: (currentProduct.variants?.find(v => v.id === selectedVariant)?.price || 0) / 100,
    });
    onClose();
  };

  // ---- mock-up preview helpers ----
  const handleGenerateMockup = async () => {
    if (!currentProduct || !selectedVariant || !selectedImageUrl) return;
    try {
      setMockupLoading(true);
      setMockupImage('');
      const task = await generateMockup(currentProduct.id, [selectedVariant], selectedImageUrl);
      setMockupTaskId(task?.mockup_task_id || task?.task_id || task?.id);
    } catch (err) {
      console.error('Mock-up generation error', err);
      alert('Failed to start mock-up generation');
      setMockupLoading(false);
    }
  };

  useEffect(() => {
    if (!mockupTaskId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getMockupStatus(mockupTaskId);
        if (cancelled) return;
        if (status.status === 'completed' && status?.mockups?.length) {
          setMockupImage(status.mockups[0].mockup_url || status.mockups[0].src || status.mockups[0].url);
          setMockupLoading(false);
          setMockupTaskId(null);
        } else if (status.status === 'failed') {
          setMockupLoading(false);
          setMockupTaskId(null);
          alert('Mock-up generation failed');
        } else {
          setTimeout(poll, 3000);
        }
      } catch (err) {
        console.error('Polling error', err);
        setTimeout(poll, 5000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [mockupTaskId]);

  return (
    <Box>
      <Heading size="lg" mb={8}>Physical Products</Heading>
      {loadingProducts ? (
        <Spinner />
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {printifyProducts.map((prod) => (
            <VStack
              key={prod.id}
              borderWidth="1px"
              borderRadius="md"
              p={4}
              spacing={3}
              bg="white"
              align="stretch"
              onClick={() => openProductModal(prod)}
              _hover={{ boxShadow: 'lg', cursor: 'pointer' }}
            >
              <img src={prod.images?.[0]?.src || prod.preview_url || prod.image} alt={prod.title} style={{ width: '100%', borderRadius: '6px' }} />
              <Heading size="sm" textAlign="center">{prod.title}</Heading>
              {prod.variants?.length > 0 && (
                <Text fontWeight="bold">${(prod.variants[0].price / 100).toFixed(2)}</Text>
              )}
              {prod.status && prod.status !== 'published' && (
                <Button size="xs" colorScheme="blue" onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await publishProduct(shopId, prod.id);
                    alert('Publish request sent. Refresh in a minute.');
                  } catch (err) {
                    console.error(err);
                    alert('Failed to publish product');
                  }
                }}>Publish</Button>
              )}
              <Button size="sm" colorScheme="green" onClick={(e) => { e.stopPropagation(); openProductModal(prod); }}>Customize</Button>
              {/* OPTIONAL – delete block if you don't use features */}
              {prod.features?.map((f) => (
                <HStack key={f}>
                  <FaCheckCircle />
                  <Text fontSize="sm">{f}</Text>
                </HStack>
              ))}
            </VStack>
          ))}
        </SimpleGrid>
      )}

      {/* Customization Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Customize Product</ModalHeader>
          <ModalBody>
            {currentDetails ? (
              <VStack align="stretch" spacing={4}>
                {currentDetails?.images?.map(img => (
                  <ChakraImage key={img.id} src={img.src} borderRadius="md" />
                ))}
                <Select value={selectedVariant} onChange={(e) => setSelectedVariant(Number(e.target.value))}>
                  {currentDetails.variants?.filter(v => v.is_enabled).map(v => (
                    <option key={v.id} value={v.id}>{v.title} – ${(v.price / 100).toFixed(2)}</option>
                  ))}
                </Select>
                <Heading size="sm" mt={4}>Choose an image from your Media Day gallery</Heading>
                {userImages.length === 0 && <Text fontSize="sm" color="gray.500">No uploads found yet – go to Dashboard &gt; Media Day Pics to add some!</Text>}
                <SimpleGrid columns={{ base: 3, md: 4 }} spacing={2}>
                  {userImages.map((u) => (
                    <Box key={u}>
                      <ChakraImage
                        src={u}
                        borderRadius="md"
                        cursor="pointer"
                        border={selectedImageUrl === u ? '3px solid #38A169' : '1px solid rgba(0,0,0,0.15)'}
                        onClick={() => setSelectedImageUrl(u)}
                        _hover={{ opacity: 0.85 }}
                      />
                    </Box>
                  ))}
                </SimpleGrid>
                <Heading size="sm" mt={4}>…or paste an external image URL</Heading>
                <Input value={selectedImageUrl} onChange={(e) => setSelectedImageUrl(e.target.value)} placeholder="https://…" />

                {mockupImage && <ChakraImage src={mockupImage} borderRadius="md" />}
                {mockupLoading && (
                  <HStack><Spinner size="sm" /><Text>Generating preview…</Text></HStack>
                )}
                <Button colorScheme="blue" onClick={handleGenerateMockup} isDisabled={!selectedVariant || !selectedImageUrl || mockupLoading}>
                  {mockupLoading ? 'Generating…' : 'Generate Preview'}
                </Button>

                <Text whiteSpace="pre-wrap">{currentDetails.description}</Text>
              </VStack>
            ) : <Spinner />}
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={handleAddToCartPrintify} isDisabled={!selectedVariant || !selectedImageUrl}>Add to Cart</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProductsPage; 